/* eslint-disable complexity */
import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import Card from "@/components/wpl/card/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

const AdditionalResourcesSection = () => {
    const intl = useIntl();

    return (
        <section
            css={css({
                padding: "32px 0 16px",
                backgroundImage:
                    "linear-gradient(180deg, #deebff 0%, #ffffff 100%)"
            })}
        >
            <h2
                css={css({
                    color: "#253858",
                    margin: "0",
                    padding: "16px 16px 40px",
                    textAlign: "center",
                    fontWeight: "500",

                    [media.below.sm]: {
                        fontSize: `${token("space.300")}`,
                        paddingBlockEnd: "30px"
                    },

                    [media.above.sm]: {
                        fontSize: "2.25rem"
                    }
                })}
            >
                {intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.additional-resources.header",
                    defaultMessage: "Guiding your team to success from day one"
                })}
            </h2>
            <div
                css={css({
                    display: "flex",
                    flexWrap: "wrap",
                    maxWidth: "1200px",
                    margin: "auto"
                })}
            >
                <AdditionalResourcesCard
                    headerText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-community.header",
                        defaultMessage: "Atlassian Community"
                    })}
                    bodyText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-community.body",
                        defaultMessage:
                            "Connect with others users to maximize your Confluence experience and share feedback for future updates."
                    })}
                    imageLink="https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:204dd0de-73fd-4223-9183-889c591309d7/8_Community%20Illo.svg?cdnVersion=1271"
                    href="https://community.atlassian.com/t5/Confluence/ct-p/confluence"
                    imageAltText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-community.image-alt-text",
                        defaultMessage:
                            "Illustrated speech bubbles with a yellow star"
                    })}
                    linkText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-community.link-text",
                        defaultMessage: "Check out our Community"
                    })}
                    dataLabelEnglish="Check out our Community"
                />
                <AdditionalResourcesCard
                    headerText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-university.header",
                        defaultMessage: "Atlassian University"
                    })}
                    bodyText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-university.body",
                        defaultMessage:
                            "Admins and regular users boost their Confluence IQ with on-demand learning for all proficiency levels."
                    })}
                    imageLink="https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:3a590ca5-2e63-4a63-8545-f13d1be6a865/8_University%20Illo.svg?cdnVersion=1271"
                    href="https://university.atlassian.com/student/catalog/list?category_ids=21564-confluence"
                    imageAltText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-university.image-alt-text",
                        defaultMessage:
                            "Illustrated purple graduation cap with a yellow tastle"
                    })}
                    linkText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.atlassian-university.link-text",
                        defaultMessage: "Get training now"
                    })}
                    dataLabelEnglish="Get training now"
                />
                <AdditionalResourcesCard
                    headerText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.support.header",
                        defaultMessage: "Support"
                    })}
                    bodyText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.support.body",
                        defaultMessage:
                            "Our team is ready from the get-go to set admins and users up for success."
                    })}
                    imageLink="https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:20c44a96-bb12-4124-a50b-d0327d9aa167/8_Support%20Illo.svg?cdnVersion=1271"
                    href="https://support.atlassian.com/confluence-cloud/"
                    imageAltText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.support.image-alt-text",
                        defaultMessage:
                            "Illustrated question and answer bubbles"
                    })}
                    linkText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.support.link-text",
                        defaultMessage: "See support options"
                    })}
                    dataLabelEnglish="See support options"
                />
                <AdditionalResourcesCard
                    headerText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.blog.header",
                        defaultMessage: "Blog"
                    })}
                    bodyText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.blog.body",
                        defaultMessage:
                            "Stay abreast of product news, and read helpful how-to’s for relevant use cases and more."
                    })}
                    imageLink="https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:05994829-2edc-47a7-869e-5d64b21f154b/8_Blog%20Illo.svg?cdnVersion=1271"
                    href="https://www.atlassian.com/blog/confluence"
                    imageAltText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.blog.image-alt-text",
                        defaultMessage:
                            "An illustrated, open book full of text content, with a yellow light bulb floating above"
                    })}
                    linkText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.additional-resources.blog.link-text",
                        defaultMessage: "Check out the blog"
                    })}
                    dataLabelEnglish="Check out the blog"
                />
            </div>
        </section>
    );
};

interface AdditionalResourcesCardProps {
    headerText: string;
    bodyText: string;
    imageLink: string;
    imageAltText: string;
    href: string;
    linkText: string;
    dataLabelEnglish: string;
}

const AdditionalResourcesCard = (props: AdditionalResourcesCardProps) => {
    const resourcesCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "additionalResourcesSection",
        "data-event": "clicked",
        "data-label": props.headerText,
        "data-label-english": props.dataLabelEnglish
    };

    return (
        <div
            css={css({
                "paddingBlockStart": "16px",
                "width": "25%",

                "div": {
                    boxShadow: "unset",
                    backgroundColor: "transparent"
                },

                "h3": {
                    textAlign: "center",
                    color: "#253858"
                },
                "p": {
                    textAlign: "center"
                },

                "img": {
                    height: "120px",
                    objectFit: "contain"
                },

                ".contentBlock-linkFlex": {
                    justifyContent: "center",
                    margin: "0"
                },

                "a": {
                    margin: "0",
                    fontWeight: "400",
                    display: "inline-block",
                    textAlign: "center",
                    lineHeight: "16px",

                    svg: {
                        position: "absolute"
                    }
                },

                [media.only.sm]: {
                    width: "50%"
                },

                [media.below.sm]: {
                    "width": "100%",

                    ".text-section h3": {
                        fontSize: `${token("space.250")}`,
                        marginBlockEnd: "14px"
                    }
                }
            })}
        >
            <Card
                headerType="h3"
                headerText={props.headerText}
                bodyText={props.bodyText}
                imageLink={props.imageLink}
                imageAltText={props.imageAltText}
                aspectRatio="none"
                ctaProps={{
                    primaryLinkProps: {
                        text: props.linkText,
                        href: props.href,
                        type: "basic-underline",
                        doesArrowAnimate: true,
                        customLinkColor: "#0052cc",
                        dataEventProps: resourcesCardDataEventProps
                    }
                }}
            />
        </div>
    );
};

export default AdditionalResourcesSection;
